/**
 * P3-10 · Downloads panel — a list of product files (spec sheets, care guides).
 * URLs are resolved server-side in the projection; a row with no resolvable URL
 * is skipped rather than rendered as a dead link.
 */
import { formatBytes } from "~/lib/media-validate";
import type { PdpDownload } from "~/server/api/schemas/pdp";

/** Short type label from a MIME type ("application/pdf" → "PDF"). */
function typeLabel(mimeType: string | null): string | null {
  if (!mimeType) return null;
  const subtype = mimeType.split("/")[1] ?? mimeType;
  return subtype.split(/[.+]/).pop()!.toUpperCase();
}

export function Downloads({ downloads }: { downloads: PdpDownload[] }) {
  const resolvable = downloads.filter(
    (d): d is PdpDownload & { url: string } => d.url !== null,
  );
  if (resolvable.length === 0) return null;

  return (
    <ul className="flex max-w-lg flex-col divide-y divide-border text-sm">
      {resolvable.map((d) => {
        const meta = [
          typeLabel(d.mimeType),
          d.sizeBytes !== null ? formatBytes(d.sizeBytes) : null,
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <li key={d.id} className="py-3">
            <a
              href={d.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 font-medium text-fg underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
            >
              <span>{d.name}</span>
              {meta && (
                <span className="shrink-0 text-xs font-normal text-fg-muted">
                  {meta}
                </span>
              )}
            </a>
          </li>
        );
      })}
    </ul>
  );
}
